import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ModelEngineProfileService, VehicleProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Enginemodel } from "assets/SideBarIcons/engine-model.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import {
  GetModelEngineByIdResponse,
  GetModelsByMakeResponse,
} from "subModule/src/services/modelengine/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import FormDialog from "common/Modal/Modal";
import MakeModal from "pages/Make/MakeModal";
import { useGetMakeDropdownService } from "subModule/src/services/make/useMake";
import ModelModal from "pages/Model/ModelModal";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function ModelEngineModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedModelEnginePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phModelEngineAr") : t("phModelEngineEn");
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vehicles",
    moduleLabelKey: "Model Engines",
    moduleHref: "/model-engines",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true
  );
  const [addNew, setAddNew] = useState<string>("");
  const [models, setModels] = useState<any>([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: makesData, refetch: makesRefetch } = useGetMakeDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm
  });
  const makes = (makesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    })
  );
  useEffect(() => {
    makesRefetch();
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const fetchModels = (make: any) => {
    ModelEngineProfileService.getModelsByMake({ page: 1, size: 50 }, make)
      .then((response: GetModelsByMakeResponse) => {
        if (response.success) {
          setModels(response?.data || {});
        } else {
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      }).finally(() => setSearchLoading(null));
  };
  const getByID = () => {
    ModelEngineProfileService.getModelEngineByID(+(params as any)?.id)
      .then((res: GetModelEngineByIdResponse) => {
        if (res.success) {
          captureLabel(res?.data?.title);
          reset({
            ...(res?.data || {}),
            makeId: { value: res.data?.makeId, label: res?.data?.makeTitle },
            modelId: { value: res.data?.modelId, label: res?.data?.modelTitle },
            isDefault: res?.data?.default
          });
          fetchModels(res.data?.makeId);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      delete data.default;
      ModelEngineProfileService.updateModelEngine(+params?.id, {
        ...data,
        makeId: data.makeId.value,
        modelId: data.modelId.value,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("modelEngine") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" }
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
          setIsBtnEnabled(true);
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        });
    } else {
      ModelEngineProfileService.createModelEngine({
        ...data,
        makeId: data.makeId.value,
        modelId: data.modelId.value,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("modelEngine") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" }
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        });
    }
  };
  const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
    if(action === 'searchIt') {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if(search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if(typeName === 'makes') makesRefetch().finally(() => setSearchLoading(null));
      else if(typeName === 'models') {
        VehicleProfileService.getModelsByMake({ page: action === 'loadNext' ? (pageNo + 1) : 1, size: 50, title: action === 'searchIt' ? search : '' }, watch('makeId')?.value)
          .then((response: GetModelsByMakeResponse) => {
            if (response.success) {
              setModels(response?.data);
            } else {
              enqueueSnackbar(<AlertMessages text={response.message} />, {
                variant: "error",
              });
            }
        }).catch((err: any) => {
          console.error("===", err);
        }).finally(() => setSearchLoading(null));
      }
    }, 300);
  }
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editModelEngines") : t("addModelEngines")}
        icon={<Enginemodel height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_MODELENGINE_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? t("editModelEngines") : t("addModelEngines")}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid
                item
                xs={12}
                md={3}
                className="grid"
                sx={{
                  gridTemplateColumns: checkPrivileges("ROLE_MAKE_CREATE") ? "85% 10%" : "100%",
                  "& .addNew path": { fill: themeColors?.primary },
                }}
              >
                <Controller
                  name="makeId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("make") + " *"}
                      {...field}
                      options={makes}
                      isSearchable
                      defaultPlaceholder="phSelectMake"
                      isLoading={searchLoading === 'makes'}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        fetchModels(selectedOption.value);
                        setValue("modelId", null);
                      }}
                      onInputChange={(search: string, { action }: { action: string }) => {
                        if(action === 'input-change' || 
                          (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                        ) handleDropdownActions("makes", 'searchIt', search);
                        else if(action === 'input-blur' || action === 'menu-close') {
                          if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("makes", 'searchIt', '');
                          else setDropdownSearchTerm('');
                          setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {if(!(makesData?.pages[0]?.data as any).last) handleDropdownActions("makes", 'loadNext')}}
                      error={errors.makeId?.message as string}
                      page={pageNo}
                    />
                  )}
                />
                {checkPrivileges("ROLE_MAKE_CREATE") && (
                  <Tooltip
                    onClick={() => setAddNew("makes")}
                    title={t("addMake")}
                  >
                    <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                  </Tooltip>
                )}
                {addNew === "makes" ? (
                  <FormDialog
                    open={addNew === "makes"}
                    onClose={(event, reason) =>
                      event && reason !== "backdropClick" ? setAddNew("") : ""
                    }
                    title={t("addMake")}
                    createPopup={true}
                    fullWidth
                    hideActions={true}
                    content={
                      <MakeModal
                        setAddNew={setAddNew}
                        makesRefetch={makesRefetch}
                      />
                    }
                  />
                ) : null}
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                className="grid"
                sx={{
                  gridTemplateColumns: checkPrivileges("ROLE_MODEL_CREATE") ? "85% 10%" : "100%",
                  "& .addNew path": { fill: themeColors?.primary },
                }}
              >
                <Controller
                  name="modelId"
                  control={control}
                  defaultValue={null}
                  rules={{ required: t("This field is required.") }}
                  render={({ field }) => (
                    <DynamicSelect
                      label={t("Model") + " *"}
                      {...field}
                      options={models?.content?.map((item: { id: number; title: string }) => ({ value: item.id, label: item.title, })) || []}
                      isSearchable
                      isDisabled={!watch("makeId")?.value}
                      defaultPlaceholder="phSelectModel"
                      isLoading={searchLoading === 'models'}
                      onChange={(selectedOption: any) => field.onChange(selectedOption)}
                      onInputChange={(search: string, { action }: { action: string }) => {
                        if(action === 'input-change' || 
                          (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                        ) handleDropdownActions("models", 'searchIt', search);
                          else if(action === 'input-blur' || action === 'menu-close') {
                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("models", 'searchIt', '');
                            else setDropdownSearchTerm('');
                            setPageNo(1);
                          }
                      }}
                      onMenuScrollToBottom={() => {if(!models?.last) handleDropdownActions("models", 'loadNext')}}
                      error={errors.modelId?.message as string}
                      page={pageNo}
                    />
                  )}
                />
                {checkPrivileges("ROLE_MODEL_CREATE") && (
                  <Tooltip
                    onClick={() => setAddNew("models")}
                    title={t("addModels")}
                  >
                    <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                  </Tooltip>
                )}
                {addNew === "models" ? (
                  <FormDialog
                    open={addNew === "models"}
                    onClose={(event, reason) =>
                      event && reason !== "backdropClick" ? setAddNew("") : ""
                    }
                    title={t("addModels")}
                    createPopup={true}
                    fullWidth
                    hideActions={true}
                    content={
                      <ModelModal
                        selectedMakeId={watch("makeId")?.value}
                        selectedMakeTitle={watch("makeId")?.label}
                        setAddNew={setAddNew}
                        makesRefetch={makesRefetch}
                        fetchModels={fetchModels}
                      />
                    }
                  />
                ) : null}
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("title", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                  placeholder={localizedModelEnginePlaceholder(primaryLanguage)}
                  title={(`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`).replace(/\s+/g, ' ').trim()}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("secondaryTitle")}
                  label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                  placeholder={localizedModelEnginePlaceholder(secondaryLanguage)}
                  title={(`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`).replace(/\s+/g, ' ').trim()}
                  fullWidth
                  error={!!errors.secondaryTitle}
                  helperText={errors.secondaryTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {checkPrivileges('ROLE_MANAGE_DEFAULT_MODELENGINE') ? <Grid item xs={12} md={3}>
                <Controller
                  name="isDefault"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (<div>
                    <CommonCheckbox
                      {...field}
                      label={t("isDefault")}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                      checked={field.value}
                      color={themeColors?.primary}
                    />
                  </div>)}
                />
              </Grid> : null}
            </Grid>
          }
        />
      )}
    </>
  );
}
